import { CloseIcon } from '@chakra-ui/icons'
import { Box, Button, Center, CircularProgress, Flex, HStack, Text } from '@chakra-ui/react'
import { Descendant, EditorDraft } from '@hb/shared'

import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ReactEditor, useSlate } from 'slate-react'
import { EditorModalContainer, EditorSize, SubmitText } from '../RichText'
import { TextEditorTemplateToolbar } from '../RichText/TemplateToolbar'
import { TextEditor } from '../RichText/TextEditor'
import { AssessmentEditorProvider } from './contexts/documentEditor'

import { DefaultModalProps } from '../Modals/types'
import { AssessmentConfigModal } from './AssessmentConfigModal'
import { TemplateInsurancePlansProvider } from './contexts'
import { useTemplateData } from './contexts/data'
import { TemplateViewProvider, TemplateViewRef, useTemplateView } from './contexts/view'
import { EditorFaxButton, useOnFax } from './EditorFaxButton'
import { EditorSaveButton, useOnSave } from './EditorSaveButton'
import { TextEditorSubmitButton, useOnSubmit } from './EditorSubmitButton'
import { ExternalPdfTemplateView } from './ExternalPdf/ExternalPdfTemplateView'
import { SelectTemplateView } from './SelectTemplateView'
import { TemplateMenu } from './TemplateMenu/TemplateMenu'
import { TemplateViewBodyProps, TemplateViewProps } from './types'
import { getWithVariables } from './withVariables'

const TemplateViewHeader = ({
  onBack,
  submitText,
}: {
  onBack?: () => void
  submitText: SubmitText | undefined
}) => {
  const {
    shortcutArgs: { plan },
  } = useTemplateData()

  const editor = useSlate()
  const {
    onFax,
    onSave,
    onSubmit,
    updateInitialText,
    editorVersion,
    template: { data: template },
    type,
    setManualTemplateId,
    templateId,
  } = useTemplateView()

  const saveManager = useOnSave(editorVersion)

  const faxManager = useOnFax(onFax)
  const submitManager = useOnSubmit(type, template, editorVersion, submitText)
  return (
    <HStack align="center" width="100%" px={2} py={2} zIndex={4} bg="white">
      <Box pr={2} minW="0" flex={1}>
        <TemplateMenu
          insuranceProviderId={plan?.insuranceProviderId ?? null}
          templateId={templateId}
          onChange={({ template, practiceId }) => {
            // manualTemplateSelectedOn.current = Date.now()
            setManualTemplateId({ templateId: template.id, practiceId })
            if (updateInitialText)
              updateInitialText(
                editorVersion || 'v1',
                template.templateText && Array.isArray(template.templateText)
                  ? template.templateText
                  : [],
              )
          }}
          templateType={type}
        />
      </Box>
      <Flex gap={1}>
        {onFax && !submitManager.submitConfirm ? (
          <EditorFaxButton editor={editor} {...faxManager} />
        ) : null}
        {onSubmit && !faxManager.faxSending ? (
          <TextEditorSubmitButton template={template} {...submitManager} />
        ) : null}
        {onSave && !submitManager.submitConfirm ? <EditorSaveButton {...saveManager} /> : null}
        {onBack ? (
          <Button
            variant="outline"
            onClick={onBack}
            size="xs"
            bg="gray.50"
            color="gray.500"
            alignItems="center"
            gap={1}>
            <CloseIcon position="relative" top="1px" w={2} />
            <Text>Close</Text>
          </Button>
        ) : null}
      </Flex>
    </HStack>
  )
}

const RichTextTemplateView = ({
  editorSize,
  submitText,
  onBack,
}: {
  editorSize: EditorSize
  submitText: SubmitText | undefined
  onBack?: () => void
}) => {
  const { initialText, editorVersion, type, readOnly } = useTemplateView()
  const initTextUpdatedOn = useRef(Date.now())

  const [initText, setInitialText] = useState(initialText)
  const [isRestarting, setIsRestarting] = useState(false)

  const t0 = useRef<ReturnType<typeof setTimeout> | null>(null)
  const restartWithText = useCallback((text: Descendant[]) => {
    setIsRestarting(true)
    if (t0.current) clearTimeout(t0.current)
    setInitialText(text)
    t0.current = setTimeout(() => {
      setIsRestarting(false)
    }, 250)
  }, [])

  useEffect(() => {
    if (initialText?.length) {
      initTextUpdatedOn.current = Date.now()
      restartWithText(initialText && Array.isArray(initialText) ? initialText : [])
    }
  }, [restartWithText, initialText])

  const ref = useRef<ReactEditor>(null)

  const decorators = useMemo(() => [getWithVariables(editorVersion)], [editorVersion])

  const toolbars = useMemo(() => [TextEditorTemplateToolbar], [])
  return isRestarting ? (
    <Center gap={2} w={`${editorSize.modalWidth}px`} h={`${editorSize.height}px`} p={4}>
      <CircularProgress color="green.500" size={8} isIndeterminate />
      <Text>Loading template...</Text>
    </Center>
  ) : (
    <TextEditor
      withDownload
      version={editorVersion}
      decorators={decorators}
      toolbars={toolbars}
      readOnly={readOnly}
      templateType={type}
      value={initText}
      ref={ref}
      {...editorSize}>
      <TemplateViewHeader onBack={onBack} submitText={submitText} />
    </TextEditor>
  )
}

const TemplateViewBody: React.FC<TemplateViewBodyProps> = ({ editorSize, onBack, submitText }) => {
  const { template: templateData, type, templateId } = useTemplateView()
  const { data: template } = templateData
  // consent forms and practice contracts have no auto-selected template — require the user
  // to pick one first
  if ((type === 'consentForm' || type === 'practiceContract') && !templateId) {
    return (
      <SelectTemplateView
        height={editorSize.height}
        width={editorSize.modalWidth}
        onClose={onBack}
      />
    )
  }

  if (template?.isExternalPdf) {
    return (
      <ExternalPdfTemplateView
        submitText={submitText}
        height={editorSize.height}
        width={editorSize.width}
        modalWidth={editorSize.modalWidth}
        onClose={onBack}
      />
    )
  }
  return <RichTextTemplateView editorSize={editorSize} submitText={submitText} onBack={onBack} />
}

interface AssessmentTemplateViewProps {
  size: EditorSize
  submitText: SubmitText | undefined
  draft?: EditorDraft | null
}

export const TemplateViewModal: React.FC<
  Omit<TemplateViewProps, 'editorSize'> & {
    isOpen: boolean
    onClose: () => void
    modalProps?: Omit<DefaultModalProps, 'render' | 'isOpen' | 'onClose'>
  }
> = ({ onClose, isOpen, modalProps, submitText, ...providerProps }) => (
  <EditorModalContainer
    onClose={onClose}
    isOpen={isOpen}
    closeDisabled={!providerProps.readOnly}
    render={(editorSize, onBack) => (
      <TemplateViewProvider {...providerProps}>
        <TemplateViewBody onBack={onBack} editorSize={editorSize} submitText={submitText} />
      </TemplateViewProvider>
    )}
    {...modalProps}
  />
)

export const AssessmentTemplateView = forwardRef<TemplateViewRef, AssessmentTemplateViewProps>(
  ({ size, submitText, draft }, ref) => (
    <TemplateInsurancePlansProvider>
      <AssessmentEditorProvider draft={draft} ref={ref}>
        <TemplateViewBody editorSize={size} submitText={submitText} />
        <AssessmentConfigModal />
      </AssessmentEditorProvider>
    </TemplateInsurancePlansProvider>
  ),
)
